import * as _ from 'lodash-es';
import React from 'react';
import type { AllTopicsTreeListTopic } from '../topic/useAllTopics';
import { useAllTopicMap } from '../topic/useAllTopics';
import SidebarItems from './SidebarItems';
import SidebarSection from './SidebarSection';
import isNotDetached from './isNotDetached';
import { SidebarAllTopics, SidebarMyTopics } from './SidebarTopics';
import useSidebarSettings from './useSidebarSettings';
import SidebarExternalPostsSection from './SidebarExternalPostsSection';
import { SidebarItem } from '../graphql/generated/graphql';
import SidebarSectionIcon from './SidebarSectionIcon';
import {
  PremadeSections,
  useSidebarSectionsContext,
} from './SidebarSectionsProvider';
import { useAllSlimPostMap } from '../post/useAllSlimPosts';
import { isFeatureEnabled } from '../utils/features';
import EditSidebarSectionMenuItem from './EditSidebarSectionMenuItem';
import { useIsSize } from '../utils/responsive/isSize';
import Sizes from '../utils/responsive/Sizes';

interface SidebarSectionsProps {
  activeTopics: Record<string, boolean>;
  expandedTopics: Record<string, boolean>;
  allTopics: AllTopicsTreeListTopic[];
}

const SidebarSections = ({
  activeTopics,
  expandedTopics,
  allTopics,
}: SidebarSectionsProps) => {
  const allTopicsMap = useAllTopicMap();
  const allSlimPostsMap = useAllSlimPostMap();
  const isMobile = useIsSize(Sizes.SM);

  const { hiddenItems } = useSidebarSettings();
  const { sidebarSections, myTopics } = useSidebarSectionsContext();

  return (
    <>
      {sidebarSections.map((section) => {
        if (section.id === PremadeSections.ALL_TOPICS) {
          return (
            <SidebarAllTopics
              key={PremadeSections.ALL_TOPICS}
              allTopics={allTopics}
              expandedTopics={expandedTopics}
              activeTopics={activeTopics}
              hidden={hiddenItems.has(SidebarItem.AllTopics)}
            />
          );
        }

        if (section.id === PremadeSections.MY_TOPICS) {
          if (myTopics == null || myTopics.length === 0) return null;

          return (
            <SidebarMyTopics
              key={PremadeSections.MY_TOPICS}
              myTopics={myTopics}
              myTopicIds={myTopics.map((topic) => topic.id)}
              expandedTopics={expandedTopics}
              activeTopics={activeTopics}
              hidden={hiddenItems.has(SidebarItem.MyTopics)}
            />
          );
        }

        if (section.id === PremadeSections.INTEGRATIONS) {
          return (
            <SidebarExternalPostsSection key={PremadeSections.INTEGRATIONS} />
          );
        }

        const posts = _.compact(
          section.items
            .filter((item) => item.__typename === 'Post')
            .map((item) => allSlimPostsMap[item.id]),
        );

        const topics = _.compact(
          section.items.map((item) =>
            item.__typename === 'Topic' ? allTopicsMap[item.id] : null,
          ),
        ).filter(isNotDetached);

        return (
          <SidebarSection
            key={section.id}
            id={section.id}
            title={section.name || 'Section'}
            initiallyCollapsed
            icon={<SidebarSectionIcon section={section} />}
            empty={posts.length === 0 && topics.length === 0}
            menuItems={
              isFeatureEnabled('sidebar_section') &&
              !isMobile && <EditSidebarSectionMenuItem sectionId={section.id} />
            }
          >
            <SidebarItems
              activeTopics={activeTopics}
              expandedTopics={expandedTopics}
              posts={posts}
              orderIds={section.items.map((item) => item.id)}
              topics={topics}
            />
          </SidebarSection>
        );
      })}
    </>
  );
};

export default SidebarSections;
